import React, { useMemo, useState, useEffect } from 'react';
import { Chart, Series, ArgumentAxis, ValueAxis, Legend, Tooltip } from 'devextreme-react/chart';
import { TagBox } from 'devextreme-react/tag-box';
import { CardAnalytics } from '../../../components/library/card-analytics/CardAnalytics';
import { useAppSelector } from '../../../contexts/hooks';
import { EReportId } from '../../../types/types.dashboard';
import { useApiDashBoard } from '../../../api/api.dashboards';
import { useScreenSize } from '../../../utils/media-query';
import './SalesDashboardNew.scss';

// Interfaces para los datos
interface SalesRawData {
  Ord_FechaCreacion: string;
  POrd_Id: number;
  POrd_SubTotal: number;
  POrd_Descuento: number;
  POrd_ValorTotal: number;
  Se_Id: number | null;
  Se_Color: string | null;
  Se_Nombre: string | null;
  Contrato: string;
  Us_Nombre: string;
}

interface ChartDataItem {
  argument: string;
  subtotal: number;
  discount: number;
  total: number;
}

export const BoardSalesDashboard = () => {
  const { getData } = useApiDashBoard();
  const { isXSmall, isSmall } = useScreenSize();
  
  // Usar el enum para el dashboard de ventas (ID: 9)
  const dashboard = useAppSelector((x) => x.dashboards.dashboards.find((x) => x.Db_Id === 9 as EReportId));

  // Estados para filtros (ahora múltiples)
  const [selectedDay, setSelectedDay] = useState<string>('Todos');
  const [selectedServices, setSelectedServices] = useState<(number | null)[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [selectedContracts, setSelectedContracts] = useState<string[]>([]);

  // Resetear día seleccionado cuando cambien los datos
  useEffect(() => {
    setSelectedDay('Todos');
  }, [dashboard?.Data]);

  // Función de refresh
  const handleRefreshClick = () => {
    getData(9 as EReportId);
  };

  // Obtener servicios únicos de los datos reales
  const serviceOptions = useMemo(() => {
    if (!dashboard?.Data || !Array.isArray(dashboard.Data)) {
      return [];
    }

    const services = new Map();
    dashboard.Data.forEach((item: SalesRawData) => {
      if (!services.has(item.Se_Id)) {
        services.set(item.Se_Id, {
          id: item.Se_Id,
          name: item.Se_Nombre || 'Sin Servicio',
          color: item.Se_Color
        });
      }
    });
    return Array.from(services.values()).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [dashboard?.Data]);

  // Obtener usuarios únicos de los datos reales
  const userOptions = useMemo(() => {
    if (!dashboard?.Data || !Array.isArray(dashboard.Data)) {
      return [];
    }

    const uniqueUsers = dashboard.Data
      .map((item: SalesRawData) => item.Us_Nombre)
      .filter((value, index, self) => self.indexOf(value) === index && value)
      .sort();
    return uniqueUsers.map(name => ({ name }));
  }, [dashboard?.Data]);

  // Obtener contratos únicos de los datos reales
  const contractOptions = useMemo(() => {
    if (!dashboard?.Data || !Array.isArray(dashboard.Data)) {
      return [];
    }

    const uniqueContracts = dashboard.Data
      .map((item: SalesRawData) => item.Contrato)
      .filter((value, index, self) => self.indexOf(value) === index && value)
      .sort();
    return uniqueContracts.map(name => ({ name }));
  }, [dashboard?.Data]);

  // Obtener días disponibles de los datos reales (últimos 7 días con datos)
  const availableDays = useMemo(() => {
    if (!dashboard?.Data || !Array.isArray(dashboard.Data)) {
      return [];
    }

    // Agrupar por fecha y obtener días únicos (corregido para zona horaria Colombia)
    const dayDateMap = new Map<string, { date: string; dayOfWeek: string }>();
    dashboard.Data.forEach((item: SalesRawData) => {
      // Convertir a fecha de Colombia (UTC-5) para obtener el día correcto
      const date = new Date(item.Ord_FechaCreacion);
      // Ajustar por zona horaria de Colombia
      const colombiaDate = new Date(date.getTime() - (5 * 60 * 60 * 1000));
      const dayOfWeek = colombiaDate.toLocaleDateString('es-ES', { weekday: 'long', timeZone: 'America/Bogota' });
      const dateKey = colombiaDate.toISOString().split('T')[0];
      
      if (!dayDateMap.has(dayOfWeek) || dateKey > dayDateMap.get(dayOfWeek)!.date) {
        dayDateMap.set(dayOfWeek, { 
          date: dateKey, 
          dayOfWeek: dayOfWeek.charAt(0).toUpperCase() + dayOfWeek.slice(1)
        });
      }
    });

    // Convertir a array y ordenar cronológicamente
    const daysWithDates = Array.from(dayDateMap.entries()).map(([day, data]) => ({
      dayOfWeek: data.dayOfWeek,
      date: data.date,
    }));
    daysWithDates.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    return daysWithDates;
  }, [dashboard?.Data]);

  // Función para etiquetas de días (corregida para zona horaria de Colombia)
  const getDayLabel = (dayOfWeek: string, date: string) => {
    // Crear fecha en zona horaria de Colombia (UTC-5)
    const dayDate = new Date(date + 'T00:00:00-05:00');
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    // Normalizar fechas a solo día/mes/año para comparación
    const dayDateOnly = new Date(dayDate.getFullYear(), dayDate.getMonth(), dayDate.getDate());
    const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const yesterdayOnly = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate());

    if (dayDateOnly.getTime() === todayOnly.getTime()) {
      return 'Hoy';
    } else if (dayDateOnly.getTime() === yesterdayOnly.getTime()) {
      return 'Ayer';
    } else {
      return isXSmall || isSmall ? dayOfWeek.substring(0, 3) : dayOfWeek;
    }
  };

  // Procesar datos reales de la API
  const processedData = useMemo(() => {
    if (!dashboard?.Data || !Array.isArray(dashboard.Data)) {
      return {
        chartData: [],
        summary: {
          totalSubtotal: 0,
          totalDiscount: 0,
          totalFinal: 0,
          ordersCount: 0,
        },
      };
    }

    let filteredData = [...dashboard.Data];

    // Filtrar por día si no es "Todos"
    if (selectedDay !== 'Todos') {
      const selectedDayData = availableDays.find(day => day.dayOfWeek === selectedDay);
      if (selectedDayData) {
        filteredData = filteredData.filter((item: SalesRawData) => {
          const itemDate = new Date(item.Ord_FechaCreacion).toISOString().split('T')[0];
          return itemDate === selectedDayData.date;
        });
      }
    }

    // Aplicar filtros por servicios (múltiples)
    if (selectedServices.length > 0) {
      filteredData = filteredData.filter((item: SalesRawData) => 
        selectedServices.includes(item.Se_Id)
      );
    }

    // Aplicar filtros por usuarios (múltiples)
    if (selectedUsers.length > 0) {
      filteredData = filteredData.filter((item: SalesRawData) => 
        selectedUsers.includes(item.Us_Nombre)
      );
    }

    // Aplicar filtros por contratos (múltiples)
    if (selectedContracts.length > 0) {
      filteredData = filteredData.filter((item: SalesRawData) => 
        selectedContracts.includes(item.Contrato)
      );
    }

    // Agrupar por fecha para el gráfico (corregido para zona horaria Colombia)
    const dateData = new Map();
    filteredData.forEach((item: SalesRawData) => {
      const date = new Date(item.Ord_FechaCreacion);
      // Ajustar por zona horaria de Colombia (UTC-5)
      const colombiaDate = new Date(date.getTime() - (5 * 60 * 60 * 1000));
      const dateKey = colombiaDate.toISOString().split('T')[0];
      const dayOfWeek = colombiaDate.toLocaleDateString('es-ES', { weekday: 'long', timeZone: 'America/Bogota' });

      if (!dateData.has(dateKey)) {
        dateData.set(dateKey, {
          date: dateKey,
          dayOfWeek,
          subtotal: 0,
          discount: 0,
          total: 0
        });
      }

      const day = dateData.get(dateKey);
      day.subtotal += item.POrd_SubTotal;
      day.discount += item.POrd_Descuento;
      day.total += item.POrd_ValorTotal;
    });

    // Preparar datos del gráfico ordenados cronológicamente
    const chartData: ChartDataItem[] = Array.from(dateData.values())
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
      .map(day => {
        // Crear fecha en zona horaria de Colombia para comparación correcta
        const dayDate = new Date(day.date + 'T00:00:00-05:00');
        const today = new Date();
        const yesterday = new Date();
        yesterday.setDate(today.getDate() - 1);

        // Normalizar fechas para comparación
        const dayDateOnly = new Date(dayDate.getFullYear(), dayDate.getMonth(), dayDate.getDate());
        const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        const yesterdayOnly = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate());

        const isToday = dayDateOnly.getTime() === todayOnly.getTime();
        const isYesterday = dayDateOnly.getTime() === yesterdayOnly.getTime();

        let dateLabel;
        if (isToday) {
          dateLabel = 'Hoy';
        } else if (isYesterday) {
          dateLabel = 'Ayer';
        } else {
          dateLabel = dayDate.toLocaleDateString('es-ES', { month: 'short', day: 'numeric' });
        }

        const dayAbbreviation = isXSmall || isSmall 
          ? day.dayOfWeek.substring(0, 3) 
          : day.dayOfWeek.substring(0, 3);
        
        return {
          argument: `${dayAbbreviation}\n${dateLabel}`,
          subtotal: day.subtotal,
          discount: day.discount,
          total: day.total
        };
      });

    // Calcular totales
    const summary = {
      totalSubtotal: filteredData.reduce((sum, item) => sum + item.POrd_SubTotal, 0),
      totalDiscount: filteredData.reduce((sum, item) => sum + item.POrd_Descuento, 0),
      totalFinal: filteredData.reduce((sum, item) => sum + item.POrd_ValorTotal, 0),
      ordersCount: filteredData.length
    };

    return { chartData, summary };
  }, [dashboard?.Data, selectedDay, selectedServices, selectedUsers, selectedContracts, availableDays, isXSmall, isSmall]);

  return (
    <CardAnalytics 
      title='Reporte de Ventas' 
      contentClass='sales-dashboard' 
      isLoading={dashboard?.Loading} 
      onRefreshClick={handleRefreshClick}
    >
      <div className='sales-container'>
        {/* Primera línea: Filtros y métricas */}
        <div className={`sales-header-line ${!isXSmall && !isSmall ? 'desktop-layout' : ''}`}>
          <div className='sales-filters-section'>
            <div className='filter-group'>
              <TagBox
                dataSource={serviceOptions}
                displayExpr="name"
                valueExpr="id"
                placeholder="Todos los servicios"
                showClearButton={true}
                searchEnabled={true}
                value={selectedServices}
                onValueChanged={(e) => setSelectedServices(e.value || [])}
                multiline={false}
                showSelectionControls={true}
                dropDownOptions={{
                  width: 350,
                  maxHeight: 300
                }}
              />
              <TagBox
                dataSource={userOptions}
                displayExpr="name"
                valueExpr="name"
                placeholder="Todos los usuarios"
                showClearButton={true}
                searchEnabled={true}
                value={selectedUsers}
                onValueChanged={(e) => setSelectedUsers(e.value || [])}
                multiline={false}
                showSelectionControls={true}
                dropDownOptions={{
                  width: 350,
                  maxHeight: 300
                }}
              />
              <TagBox
                dataSource={contractOptions}
                displayExpr="name"
                valueExpr="name"
                placeholder="Todos los contratos"
                showClearButton={true}
                searchEnabled={true}
                value={selectedContracts}
                onValueChanged={(e) => setSelectedContracts(e.value || [])}
                multiline={false}
                showSelectionControls={true}
                dropDownOptions={{
                  width: 350,
                  maxHeight: 300
                }}
              />
            </div>
          </div>

          {/* Métricas */}
          <div className={`sales-metrics-section ${!isXSmall && !isSmall ? 'horizontal-metrics' : 'vertical-metrics'}`}>
            <div className='metric-item subtotal'>
              <span className='metric-number'>${processedData.summary.totalSubtotal.toLocaleString()}</span>
              <span className='metric-text'>SUBTOTAL</span>
            </div>
            <div className='metric-item discount'>
              <span className='metric-number'>${processedData.summary.totalDiscount.toLocaleString()}</span>
              <span className='metric-text'>DESCUENTOS</span>
            </div>
            <div className='metric-item total'>
              <span className='metric-number'>${processedData.summary.totalFinal.toLocaleString()}</span>
              <span className='metric-text'>TOTAL</span>
            </div>
            <div className='metric-item orders'>
              <span className='metric-number'>{processedData.summary.ordersCount}</span>
              <span className='metric-text'>ÓRDENES</span>
            </div>
          </div>
        </div>

        {/* Segunda línea: Chips de días */}
        <div className='sales-days-line'>
          <div className='days-chips'>
            <button 
              className={`day-chip compact ${selectedDay === 'Todos' ? 'active' : ''}`} 
              onClick={() => setSelectedDay('Todos')}
            >
              Todos
            </button>
            {availableDays.map((dayData) => (
              <button 
                key={dayData.dayOfWeek} 
                className={`day-chip compact ${selectedDay === dayData.dayOfWeek ? 'active' : ''}`} 
                onClick={() => setSelectedDay(dayData.dayOfWeek)}
              >
                {getDayLabel(dayData.dayOfWeek, dayData.date)}
              </button>
            ))}
          </div>
        </div>

        {/* Gráfico */}
        <div className='chart-container-expanded'>
          {processedData.chartData.length > 0 ? (
            <Chart 
              dataSource={processedData.chartData} 
              height={isXSmall ? 180 : isSmall ? 200 : 220} 
              palette={['#9C88FF', '#FF6B6B', '#4ECDC4']}
            >
              <ArgumentAxis
                discreteAxisDivisionMode='crossLabels'
                label={{
                  rotationAngle: isXSmall ? -45 : 0,
                  overlappingBehavior: 'rotate',
                }}
              />
              <ValueAxis
                label={{                    
                  format: {
                    type: 'currency',
                    precision: 0,
                  },
                }}
              />

              <Series valueField='subtotal' argumentField='argument' name='Subtotal' type='bar' color='#9C88FF' />
              <Series valueField='discount' argumentField='argument' name='Descuentos' type='bar' color='#FF6B6B' />
              <Series valueField='total' argumentField='argument' name='Total' type='bar' color='#4ECDC4' />

              <Legend visible={!isXSmall} verticalAlignment='bottom' horizontalAlignment='center' />

              <Tooltip
                enabled={true}
                customizeTooltip={(arg) => ({
                  text: `${arg.argument}<br/><strong>${arg.seriesName}</strong>: $${arg.value.toLocaleString()}`,
                })}
              />
            </Chart>
          ) : (
            <div className='no-data'>
              <div className='no-data-icon'>💰</div>
              <div className='no-data-text'>No hay datos de ventas para mostrar</div>
            </div>
          )}
        </div>
      </div>
    </CardAnalytics>
  );
};